import { useState, useEffect, useCallback } from "react";
import Card from "../../components/Card/Card";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import { enqueueSnackbar } from "notistack";
import HomeworkForm from "./HomeworkForm";
import {
  getHomeworkById,
  createHomework,
  updateHomework,
} from "../../services/HomeworkService";
import { getTeacherSubjectMapping } from "../../services/TeacherSubjectMappingService";

const HomeworkManage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [id, setId] = useState(null);
  const [loader, setLoader] = useState(false);
  const [program_id, setProgramId] = useState("");
  const [subject_id, setSubjectId] = useState("");
  const [start_date, setStartDate] = useState("");
  const [end_date, setEndDate] = useState("");
  const [homework_type, setHomeworkType] = useState("");
  const [homework_title, setHomeworkTitle] = useState("");
  const [homework_description, setHomeworkDescription] = useState("");
  const [homework_attachment, setHomeworkAttachment] = useState("");
  const [homework_attachment_view, setHomeworkAttachmentView] = useState("");
  const [assigned_date, setAssignedDate] = useState("");
  const [programData, setProgramData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [programs, setPrograms] = useState([]);

  const fetchHomework = useCallback(
    async (programsData) => {
      if (!params.id) return;
      setLoader(true);
      try {
        const response = await getHomeworkById(params.id);
        if (response.status === 200) {
          const {
            id,
            program_id,
            subject_id,
            start_date,
            end_date,
            homework_type,
            homework_title,
            homework_description,
            assigned_date,
            homework_attachment_view,
          } = response.data.data;

          let subjects = programsData.find(
            (program) => program.id === program_id,
          )?.subjects;
          setSubjectData(subjects);
          setId(id);
          setProgramId(program_id);
          setSubjectId(subject_id);
          setStartDate(start_date);
          setEndDate(end_date);
          setHomeworkType(homework_type);
          setHomeworkTitle(homework_title);
          setHomeworkDescription(homework_description);
          setHomeworkAttachmentView(homework_attachment_view);
          setAssignedDate(assigned_date);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoader(false);
      }
    },
    [params.id],
  );

  const fetchTeacherSubjects = useCallback(async () => {
    try {
      const user_id = JSON.parse(localStorage.getItem("user")).id;
      const response = await getTeacherSubjectMapping(user_id);

      if (response.status === 200) {
        const list = response.data.data;
        let programList = list.programs || [];
        if (list.class_in_charge && list.class_in_charge.length > 0) {
          let programs = list.class_in_charge.map((item) => ({
            id: item.id,
            name: item.name,
            subjects: item.subjects || [],
          }));
          programList = [...programList, ...programs];
        }
        setProgramData(programList);
        if (params.id) fetchHomework(programList || []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [fetchHomework, params.id]);

  useEffect(() => {
    fetchTeacherSubjects();
  }, [fetchTeacherSubjects]);

  const programChangeHandler = (item) => {
    setPrograms(item);
  };

  const programRemoveHandler = (item) => {
    setPrograms(item);
  };

  const changeHandler = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case "program_id": {
        setProgramId(value);
        const selectedProgram = programData.find(
          (program) => program.id.toString() === value,
        );
        if (selectedProgram) {
          setSubjectData(selectedProgram.subjects);
        }
        break;
      }
      case "subject_id":
        setSubjectId(value);
        break;
      case "start_date":
        setStartDate(value);
        break;
      case "end_date":
        setEndDate(value);
        break;
      case "homework_type":
        setHomeworkType(value);
        break;
      case "homework_title":
        setHomeworkTitle(value);
        break;
      case "homework_description":
        setHomeworkDescription(value);
        break;
      case "homework_attachment":
        setHomeworkAttachment(event.target.files[0]);
        break;
      case "assigned_date":
        setAssignedDate(value);
        break;
      default:
        break;
    }
  };

  const submitHandler = async (event) => {
    event.preventDefault();
    setLoader(true);
    let formData = new FormData();
    if (homework_attachment) {
      formData.append("homework_attachment", homework_attachment);
    }
    if (id) {
      formData.append("_method", "PUT"); // 👈 force Laravel to treat as PUT
    }
    // const programIds = programs.map((program) => program.id);
    // formData.append("program_ids", programIds);
    formData.append("program_id", program_id);
    formData.append("subject_id", subject_id);
    formData.append("homework_type", homework_type);
    formData.append("homework_title", homework_title);
    formData.append("homework_description", homework_description);

    if (homework_type === "daily") {
      formData.append("assigned_date", assigned_date);
    }

    if (homework_type === "weekly") {
      formData.append("start_date", start_date);
      formData.append("end_date", end_date);
    }

    try {
      const response = id
        ? await updateHomework(id, formData)
        : await createHomework(formData);

      if (response.status === 200) {
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        navigate("/homework/list");
      }
    } catch (error) {
      console.error("Error submitting homework:", error.response);
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const back = () => {
    navigate("/homework/list");
  };

  return (
    <Card componentName={id ? "Update Homework" : "Add Homework"} back={back}>
      {loader ? (
        <Loader />
      ) : (
        <HomeworkForm
          programs={programs}
          programChangeHandler={programChangeHandler}
          programRemoveHandler={programRemoveHandler}
          onSubmitHandler={submitHandler}
          onChangeHandler={changeHandler}
          programData={programData}
          subjectData={subjectData}
          program_id={program_id}
          subject_id={subject_id}
          start_date={start_date}
          end_date={end_date}
          homework_type={homework_type}
          homework_title={homework_title}
          homework_description={homework_description}
          homework_attachment={homework_attachment}
          homework_attachment_view={homework_attachment_view}
          assigned_date={assigned_date}
        />
      )}
    </Card>
  );
};

export default HomeworkManage;
